import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Production WebSocket hook — authenticated, auto-reconnect, no page freeze.
 *
 * Features:
 *   - JWT token authentication via query param
 *   - Exponential backoff reconnection (1s → 2s → 4s → ... → 30s max)
 *   - Ping/pong keepalive (30s interval)
 *   - Channel subscription API (prices, trades, bot_status, health)
 *   - Graceful cleanup on unmount — no stale callbacks, no memory leaks
 *   - All WS work is async/event-driven — never blocks the main thread
 */

const WS_BASE =
  process.env.REACT_APP_WS_URL ||
  (window.location.protocol === "https:" ? "wss://" : "ws://") +
    (process.env.REACT_APP_API_URL
      ? new URL(process.env.REACT_APP_API_URL).host
      : "localhost:8001");

const MAX_RECONNECT_DELAY = 30000;
const INITIAL_RECONNECT_DELAY = 1000;
const PING_INTERVAL = 30000;

export function useWebSocket(path = "/ws/trading") {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);

  // Refs for lifecycle management (no stale closures)
  const unmounted = useRef(false);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const reconnectDelay = useRef(INITIAL_RECONNECT_DELAY);
  const manualClose = useRef(false);

  const cleanup = useCallback(() => {
    if (pingTimer.current) {
      clearInterval(pingTimer.current);
      pingTimer.current = null;
    }
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (ws.current) {
      // Remove handlers before closing to prevent reconnect loop
      ws.current.onopen = null;
      ws.current.onclose = null;
      ws.current.onerror = null;
      ws.current.onmessage = null;
      if (
        ws.current.readyState === WebSocket.OPEN ||
        ws.current.readyState === WebSocket.CONNECTING
      ) {
        ws.current.close(1000, "cleanup");
      }
      ws.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (unmounted.current) return;

    // Get auth token
    const token = localStorage.getItem("token");
    if (!token) {
      // No token — don't connect, retry in 5s (user may not be logged in yet)
      reconnectTimer.current = setTimeout(() => {
        if (!unmounted.current) connect();
      }, 5000);
      return;
    }

    cleanup();
    manualClose.current = false;

    const url = `${WS_BASE}${path}?token=${encodeURIComponent(token)}`;

    try {
      ws.current = new WebSocket(url);
    } catch (err) {
      console.warn("[WS] Failed to create WebSocket:", err);
      scheduleReconnect();
      return;
    }

    ws.current.onopen = () => {
      if (unmounted.current) return;
      setIsConnected(true);
      setError(null);
      reconnectDelay.current = INITIAL_RECONNECT_DELAY; // Reset backoff on success

      // Start keepalive pings
      pingTimer.current = setInterval(() => {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
          ws.current.send(JSON.stringify({ type: "ping", timestamp: Date.now() }));
        }
      }, PING_INTERVAL);
    };

    ws.current.onmessage = (event) => {
      if (unmounted.current) return;
      try {
        const data = JSON.parse(event.data);
        // Don't update state for pong messages — saves re-renders
        if (data.type === "pong") return;
        setLastMessage(data);
      } catch {
        // Non-JSON message — ignore silently
      }
    };

    ws.current.onerror = () => {
      // onerror is always followed by onclose — handle reconnect there
      if (!unmounted.current) {
        setError("WebSocket error");
      }
    };

    ws.current.onclose = (event) => {
      if (unmounted.current) return;

      setIsConnected(false);
      if (pingTimer.current) {
        clearInterval(pingTimer.current);
        pingTimer.current = null;
      }

      // Don't reconnect on auth failure (4001) or manual close
      if (event.code === 4001) {
        setError("Authentication failed — please log in again");
        return;
      }
      if (manualClose.current) return;

      scheduleReconnect();
    };
  }, [path, cleanup]);

  const scheduleReconnect = useCallback(() => {
    if (unmounted.current || manualClose.current) return;

    const delay = reconnectDelay.current;
    reconnectDelay.current = Math.min(delay * 2, MAX_RECONNECT_DELAY);

    reconnectTimer.current = setTimeout(() => {
      if (!unmounted.current) connect();
    }, delay);
  }, [connect]);

  // Connect on mount, cleanup on unmount
  useEffect(() => {
    unmounted.current = false;
    connect();

    return () => {
      unmounted.current = true;
      manualClose.current = true;
      cleanup();
    };
  }, [connect, cleanup]);

  // Reconnect when token changes (login/logout)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "token") {
        // Token changed — reconnect with new auth
        cleanup();
        reconnectDelay.current = INITIAL_RECONNECT_DELAY;
        setTimeout(() => {
          if (!unmounted.current) connect();
        }, 500);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [connect, cleanup]);

  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(typeof message === "string" ? message : JSON.stringify(message));
    }
  }, []);

  // Convenience: subscribe to a channel
  const subscribe = useCallback(
    (channel, options = {}) => {
      sendMessage({ type: "subscribe", channel, ...options });
    },
    [sendMessage]
  );

  // Convenience: watch specific price symbols
  const watchPrices = useCallback(
    (symbols) => {
      sendMessage({ type: "watch_prices", symbols });
    },
    [sendMessage]
  );

  const disconnect = useCallback(() => {
    manualClose.current = true;
    cleanup();
    setIsConnected(false);
  }, [cleanup]);

  return {
    isConnected,
    lastMessage,
    error,
    sendMessage,
    subscribe,
    watchPrices,
    disconnect,
  };
}
